"use client";

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import styles from "./Reveal.module.css";

export type RevealProps = {
  children: ReactNode;
  className?: string;
  /** Distance de départ du glissement vers le haut (le site : 100 px). */
  distance?: number;
  /** Durée (le site : 1000 ms avec le rebond « back.out »). */
  duration?: number;
  /** Retard avant le départ, en ms (le site double le délai : on fait pareil). */
  delay?: number;
  /** Une seule fois ; sinon l'entrée rejoue quand l'élément repasse sous l'écran (comme le site). */
  once?: boolean;
};

/**
 * Entrée « slideUp » du site : l'élément glisse de 100 px vers le haut en
 * apparaissant, avec un léger rebond à la fin (easeOutBack), dès que ses
 * 120 premiers pixels entrent dans l'écran. Rien avec prefers-reduced-motion,
 * et tout reste visible sans JavaScript (l'état caché n'est posé qu'au montage).
 */
export function Reveal({ children, className, distance = 100, duration = 1000, delay = 0, once = false }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) {
      el.dataset.reveal = "shown";
      return;
    }
    el.dataset.reveal = "before";
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.dataset.reveal = "shown";
            if (once) observer.disconnect();
          } else if (!once && entry.boundingClientRect.top >= window.innerHeight) {
            el.dataset.reveal = "before";
          }
        }
      },
      { rootMargin: "0px 0px -120px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const style = { "--reveal-distance": `${distance}px`, "--reveal-duration": `${duration}ms`, "--reveal-delay": `${delay * 2}ms` } as CSSProperties;
  return (
    <div ref={ref} className={[styles.reveal, className].filter(Boolean).join(" ")} style={style}>
      {children}
    </div>
  );
}
